import './App.css';
import MobileMenu from './MobileMenu';
import Sidebar from './Sidebar';
import { motion } from 'framer-motion';
import { LinkedInIcon, GitHubIcon, MediumIcon, ArrowRightIcon } from './Icons';
import ProjectCard from './ProjectCard';
import PayChalkVignette from './PayChalkVignette';
import JenesysVignette from './JenesysVignette';
import TikitiVignette from './TikitiVignette';
import TikitiDashboardVignette from './TikitiDashboardVignette';
import KaakaVignette from './KaakaVignette';
import ChoperlyVignette from './ChoperlyVignette';
import StudioStrip from './StudioStrip';
import GridSquares from './GridSquares';
import { BOOKING_URL, EMAIL } from './links';
import { useEffect, useState } from 'react';

function Portfolio() {
  const [currentTime, setCurrentTime] = useState('');
  const [weather, setWeather] = useState({ temp: '--', condition: 'Loading...' });
  const [currentQuote, setCurrentQuote] = useState(0);

  const quotes = [
    "The best products ship, not sit in Figma.",
    "Simplicity is the ultimate sophistication.",
    "Good design solves the problem. Great design removes it.",
    "The details are not the details. They make the product.",
    "Strategy without execution is a slideshow."
  ];

  // Update time every second (Accra timezone - GMT)
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const options = {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: 'Africa/Accra'
      };
      setCurrentTime(now.toLocaleTimeString('en-US', options));
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  // Rotate quotes every 8 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentQuote((prev) => (prev + 1) % quotes.length);
    }, 8000);
    return () => clearInterval(interval);
  }, [quotes.length]);

  // Fetch weather for Accra
  useEffect(() => {
    const fetchWeather = async () => {
      try {
        // Using wttr.in for simple weather data (no API key needed)
        const response = await fetch('https://wttr.in/Accra?format=%t+%C');
        if (!response.ok) throw new Error('weather unavailable');
        const data = await response.text();
        const parts = data.trim().split(' ');
        const temp = parts[0] || '';
        // wttr.in returns an HTML error page when rate-limited — only accept
        // values that actually look like a temperature (e.g. "+28°C")
        if (!/^[+-]?\d{1,3}°[CF]$/.test(temp)) throw new Error('bad payload');
        const condition = parts.slice(1).join(' ') || 'Clear';
        setWeather({ temp: temp.replace(/^\+/, ''), condition });
      } catch (error) {
        setWeather({ temp: '28°C', condition: 'Sunny' }); // Fallback for Accra
      }
    };

    fetchWeather();
    // Refresh weather every 30 minutes
    const interval = setInterval(fetchWeather, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Project data - main case studies
  const mainProjects = [
    {
      id: 0,
      title: 'Stimulir',
      description: 'Product Engineer across console UX, website, onboarding, developer experience, and design systems.',
      link: '/stimulir-case-study',
      bgColor: '#1a1a1a',
      image: '/images/case-studies/stimulir/console-traces.png',
      dark: true,
    },
    {
      id: 1,
      title: 'Jenesys',
      description: 'Designed the product experience for an AI bookkeeping platform that reached $1M ARR and 80% feature adoption.',
      link: '/case-study',
      bgColor: '#e4dbea',
      image: '/images/case-studies/jenesys-dashboard.png'
    },
    {
      id: 2,
      title: 'Tikiti',
      description: 'Designed and shipped an event ticketing app with 1.7k downloads. Event discovery, mobile payments, QR scanning.',
      link: '/tikiti-case-study',
      bgColor: '#c8d9f0',
      image: '/images/case-studies/tikiti-screen-5.png'
    },
    {
      id: 3,
      title: 'Tikiti Dashboard',
      description: 'Designed and shipped a B2B event management platform. Multi-channel messaging, attendee management, and AI reports.',
      link: '/tikiti-dashboard-case-study',
      bgColor: '#d6e4f0',
      image: '/images/tikiti-dashboard-hero.png'
    },
    {
      id: 4,
      title: 'PayChalk',
      description: 'Designed and built a WhatsApp-first school fee collection platform for Ghana. Instant payments, automated reminders, real-time tracking.',
      link: '/paychalk-case-study',
      bgColor: '#e8f0e4',
      image: '/images/case-studies/paychalk-dashboard.png'
    },
    {
      id: 5,
      title: 'Kaaka',
      description: 'Designed and shipped an AI cooking assistant that turns grocery receipts into personalized meal plans.',
      link: '/kaaka-case-study',
      bgColor: '#e0ddd5',
      image: '/images/case-studies/kaaka-home.png'
    },
    {
      id: 6,
      title: 'SheFundIt',
      description: 'Designed and shipped a milestone-based crowdfunding platform empowering women entrepreneurs to fund their businesses.',
      link: '/shefundit-case-study',
      bgColor: '#e8f5e9',
      image: '/images/case-studies/shefundit-screen-7.png'
    },
  ];

  return (
    <div className="App">
      {/* Top Header Bar - Desktop Only */}
      <header className="top-header-bar">
        {/* Quote - Center */}
        <div className="header-quote">
          <motion.span
            key={currentQuote}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            "{quotes[currentQuote]}"
          </motion.span>
        </div>

        {/* Weather & Time - Right */}
        <div className="header-weather-time">
          <div className="header-item">
            <span className="header-label">Accra</span>
            <span className="header-value">{weather.temp}</span>
          </div>
          <div className="header-divider" />
          <div className="header-item">
            <span className="header-label">Local</span>
            <span className="header-value">{currentTime}</span>
          </div>
        </div>
      </header>

      {/* Sidebar Navigation (desktop only) - includes brand logo */}
      <Sidebar />

      {/* Mobile Menu */}
      <MobileMenu />

      {/* Hero Section */}
      <main>
      <section className="hero-redesign">
        <div className="hero-content-centered">
          <div className="hero-text-group">
            <motion.div
              className="hero-badge-pill"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              For founders and teams shipping AI-native products
            </motion.div>

            <motion.h1
              className="hero-headline-large"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              I find the signal, design the product, and ship it.
            </motion.h1>

            <motion.p
              className="hero-subheadline"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              I take products from the first research call to the code that ships, then stay to scale what works — across AI, fintech, and B2B SaaS.
            </motion.p>

            <motion.div
              className="hero-proof-row"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
            >
              <div className="hero-proof-item">
                <span className="hero-proof-number">4</span>
                <span className="hero-proof-label">products live today</span>
              </div>
              <div className="hero-proof-item">
                <span className="hero-proof-number">$1M ARR</span>
                <span className="hero-proof-label">platform designed</span>
              </div>
              <div className="hero-proof-item">
                <span className="hero-proof-number">1.7k</span>
                <span className="hero-proof-label">downloads shipped solo</span>
              </div>
              <div className="hero-proof-item">
                <span className="hero-proof-number">7+ yrs</span>
                <span className="hero-proof-label">in product</span>
              </div>
            </motion.div>

            <motion.div
              className="hero-cta-row"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
            >
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="hero-cta-primary">
                <span className="live-dot" />
                Book a 30-min intro call
                <svg width="13" height="13" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                  <path d="M3 9L9 3M9 3H4.5M9 3V7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </a>
              <span className="hero-cta-note">Free · no commitment · for contracts and projects</span>
            </motion.div>
          </div>

          {/* Previously At and Featured In sections - hidden for now
          <motion.div
            className="hero-companies"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            <span className="previously-at">Previously at</span>
            <div className="company-logos">
              <img src="/images/logos/jenesys-logo.png" alt="Jenesys" />
              <img src="/images/logos/jenesys-logo.png" alt="Company 2" />
              <img src="/images/logos/jenesys-logo.png" alt="Company 3" />
            </div>
          </motion.div>

          <motion.div
            className="hero-featured-in"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.0 }}
          >
            <span className="featured-in-label">Featured in</span>
            <div className="featured-logos-grid">
              <div className="featured-logo-card">
                <img src="/images/logos/jenesys-logo.png" alt="Lapa" />
              </div>
              <div className="featured-logo-card">
                <img src="/images/logos/jenesys-logo.png" alt="Landbook" />
              </div>
              <div className="featured-logo-card">
                <img src="/images/logos/jenesys-logo.png" alt="Bestfolios" />
              </div>
              <div className="featured-logo-card">
                <img src="/images/logos/jenesys-logo.png" alt="Medium" />
              </div>
              <div className="featured-logo-card">
                <img src="/images/logos/jenesys-logo.png" alt="UX Planet" />
              </div>
              <div className="featured-logo-card">
                <img src="/images/logos/jenesys-logo.png" alt="Designer Hunt" />
              </div>
              <div className="featured-logo-card">
                <img src="/images/logos/jenesys-logo.png" alt="Case Study Club" />
              </div>
              <div className="featured-logo-card">
                <img src="/images/logos/jenesys-logo.png" alt="UX Collective" />
              </div>
            </div>
          </motion.div>
          */}
        </div>
      </section>

      {/* Two-Column Project Grid */}
      <section className="project-grid-section">
        <div className="project-grid-container">
          <div className="project-grid-row">
            <div className="project-card-large">
              <ProjectCard link="/polliplus-case-study" bgColor="#e4e8e0" name="Polliplus" meta="Product Engineer · 2026" bullets={['Research, journeys, and UX across field reporting and intelligence', 'From evidence and reporting gaps to team follow-up']}>
                <img src="/images/case-studies/polliplus/silence-board.jpg" alt="Polliplus product interface showing the Silence Board" className="case-study-card-screen" loading="lazy" />
              </ProjectCard>
            </div>
            <div className="project-card-large">
              <ProjectCard link="/nsat-case-study" bgColor="#dce9e5" name="Intervia NSAT" meta="Product Engineer · 2026" bullets={['Newcomer intake, assessment, and settlement planning', 'Research-led flows across client, worker, and admin experiences']}>
                <img src="/images/case-studies/nsat/landing.jpg" alt="NSAT newcomer product entry experience" className="case-study-card-screen" loading="lazy" />
              </ProjectCard>
            </div>
          </div>
          {/* Row 1 */}
          <div className="project-grid-row">
            <motion.div
              className="project-card-large"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true, amount: 0.2 }}
            >
              <ProjectCard
                link={mainProjects[0].link}
                bgColor={mainProjects[0].bgColor}
                dark
                name="Stimulir"
                meta="Product Engineer · 2025–2026"
                bullets={[
                  'Console UX, review flows, and the shared design language',
                  'Website, onboarding, and developer experience',
                ]}
              >
                <img src="/images/case-studies/stimulir/console-traces.png" alt="Stimulir console with traces and an agent proposal" className="case-study-card-screen" loading="lazy" />
              </ProjectCard>
            </motion.div>
            <motion.div
              className="project-card-large"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true, amount: 0.2 }}
            >
              <ProjectCard
                link={mainProjects[1].link}
                bgColor={mainProjects[1].bgColor}
                name="Jenesys"
                meta="Product designer · 2023–2024"
                bullets={[
                  'Reached $1M ARR with 80% feature adoption',
                  'Designed invoicing, matching, and chaser workflows end to end',
                ]}
              >
                <JenesysVignette />
              </ProjectCard>
            </motion.div>
          </div>
          {/* Row 2 */}
          <div className="project-grid-row">
            <motion.div
              className="project-card-large"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true, amount: 0.2 }}
            >
              <ProjectCard
                link={mainProjects[2].link}
                bgColor={mainProjects[2].bgColor}
                name="Tikiti"
                meta="Founder & product designer · 2024"
                bullets={[
                  '1.7k downloads on iOS and Android',
                  'Event discovery, mobile payments, and QR check-in',
                ]}
              >
                <TikitiVignette />
              </ProjectCard>
            </motion.div>
            <motion.div
              className="project-card-large"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true, amount: 0.2 }}
            >
              <ProjectCard
                link={mainProjects[3].link}
                bgColor={mainProjects[3].bgColor}
                name="Tikiti Dashboard"
                meta="Founder & product designer · 2024–2025"
                bullets={[
                  'Multi-channel messaging and attendee management for organizers',
                  'AI-generated event reports and analytics',
                ]}
              >
                <TikitiDashboardVignette />
              </ProjectCard>
            </motion.div>
          </div>
          {/* Row 3 */}
          <div className="project-grid-row">
            <motion.div
              className="project-card-large"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true, amount: 0.2 }}
            >
              <ProjectCard
                link={mainProjects[4].link}
                bgColor={mainProjects[4].bgColor}
                name="PayChalk"
                meta="Product designer · 2025"
                bullets={[
                  'WhatsApp-first fee collection for schools in Ghana',
                  'Instant payments, automated reminders, real-time tracking',
                ]}
              >
                <PayChalkVignette />
              </ProjectCard>
            </motion.div>
            <motion.div
              className="project-card-large"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true, amount: 0.2 }}
            >
              <ProjectCard
                link={mainProjects[5].link}
                bgColor={mainProjects[5].bgColor}
                name="Kaaka"
                meta="Product designer · 2024"
                bullets={[
                  'Turns grocery receipts into personalized meal plans',
                  'Designed and shipped the full app experience',
                ]}
              >
                <KaakaVignette />
              </ProjectCard>
            </motion.div>
          </div>
          {/* Row 4 */}
          <div className="project-grid-row">
            <div className="project-card-large">
              <ProjectCard link="/aca-case-study" bgColor="#e2e8f4" name="Intervia ACA" meta="Product Engineer · 2026" bullets={['Individual and team assessment journeys, setup, and invitations', 'Results, reporting, and the UX between each stage']}>
                <img src="/images/case-studies/aca/card-preview.svg" alt="InterviaIQ competency report preview with an illustrative score and competency breakdown" className="case-study-card-screen aca-card-preview" loading="lazy" />
              </ProjectCard>
            </div>
            <motion.div
              className="project-card-large"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true, amount: 0.2 }}
            >
              <ProjectCard
                link="/choperly-case-study"
                bgColor="#ffe3f9"
                name="Choperly"
                meta="Designed & built solo · 2025"
                bullets={[
                  'WhatsApp bot handles orders, MoMo payments, and rider dispatch',
                  'AI menu onboarding — from signup to live bot in 15 minutes',
                ]}
              >
                <ChoperlyVignette />
              </ProjectCard>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Now in the Studio — in-progress work */}
      <StudioStrip />

      {/* What I Bring Section */}
      <section className="expertise-section">
        <div className="expertise-container">
          <motion.div
            className="expertise-header"
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
          >
            <h2 className="expertise-title">What I specialize in</h2>
          </motion.div>

          <div className="expertise-grid">
            <motion.div
              className="expertise-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <h3 className="expertise-card-title">0-to-1 Product Design</h3>
              <p className="expertise-card-text">I help startups turn rough ideas into real products with clear positioning, sharp user flows, and launch-ready interfaces. My work spans product discovery, UX strategy, feature prioritization, and end-to-end execution for early-stage teams.</p>
            </motion.div>

            <motion.div
              className="expertise-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h3 className="expertise-card-title">AI Product Design</h3>
              <p className="expertise-card-text">I design AI-native products that make complex systems feel understandable and useful. From confidence-driven interfaces to automation workflows and human-in-the-loop review, I focus on building trust into the product experience.</p>
            </motion.div>

            <motion.div
              className="expertise-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <h3 className="expertise-card-title">Fintech & B2B SaaS</h3>
              <p className="expertise-card-text">A lot of my strongest work sits at the intersection of fintech, operations, and B2B SaaS. I design dashboards, payment flows, reconciliation tools, and workflow-heavy products that need both clarity for users and measurable business outcomes.</p>
            </motion.div>

            <motion.div
              className="expertise-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <h3 className="expertise-card-title">Design to Production</h3>
              <p className="expertise-card-text">I use tools like Claude Code and Cursor to move from design into production-ready implementation. That means I can help teams close the gap between product strategy, interface design, and a working shipped experience.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Landing Pages Gallery Section */}
      <section className="landing-pages-section">
        <div className="landing-pages-container">
          <motion.div
            className="landing-pages-header"
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
          >
            <h2 className="landing-pages-title">Landing Pages & Websites</h2>
            <p className="landing-pages-subtitle">Designed and built for clients and personal projects</p>
          </motion.div>

          <div className="landing-pages-grid">
            {/* BeOrchid */}
            <motion.a
              href="https://beorchid.com"
              target="_blank"
              rel="noopener noreferrer"
              className="landing-page-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true, amount: 0.2 }}
            >
              <div className="landing-page-image-wrapper">
                <img src="/images/landing-pages/beorchid-landing.png" alt="BeOrchid Website" />
                <div className="landing-page-overlay">
                  <span className="view-site-btn">View Live Site <ArrowRightIcon size={16} /></span>
                </div>
              </div>
              <div className="landing-page-info">
                <h3 className="landing-page-name">BeOrchid</h3>
                <p className="landing-page-description">Technology studio website with modern aesthetics and clear service offerings</p>
              </div>
            </motion.a>

            {/* Stimulir */}
            <motion.a
              href="https://stimulir.com"
              target="_blank"
              rel="noopener noreferrer"
              className="landing-page-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true, amount: 0.2 }}
            >
              <div className="landing-page-image-wrapper">
                <img src="/images/case-studies/stimulir/website.jpg" alt="Stimulir Website" />
                <div className="landing-page-overlay">
                  <span className="view-site-btn">View Live Site <ArrowRightIcon size={16} /></span>
                </div>
              </div>
              <div className="landing-page-info">
                <h3 className="landing-page-name">Stimulir</h3>
                <p className="landing-page-description">AI-powered hiring platform that screens, ranks, and shortlists top candidates automatically</p>
              </div>
            </motion.a>

            {/* HerGuided Tours */}
            <motion.a
              href="https://herguidedtours.com"
              target="_blank"
              rel="noopener noreferrer"
              className="landing-page-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true, amount: 0.2 }}
            >
              <div className="landing-page-image-wrapper">
                <img src="/images/landing-pages/herguidedtours-landing.png" alt="HerGuided Tours Website" />
                <div className="landing-page-overlay">
                  <span className="view-site-btn">View Live Site <ArrowRightIcon size={16} /></span>
                </div>
              </div>
              <div className="landing-page-info">
                <h3 className="landing-page-name">HerGuided Tours</h3>
                <p className="landing-page-description">Curated travel experiences platform helping travelers find their paradise</p>
              </div>
            </motion.a>
          </div>
        </div>
      </section>

      {/* Contact Footer */}
      <footer className="contact-footer-simple" id="contact">
        <GridSquares />
        <div className="contact-footer-container">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center' }}
          >
            <h2 className="contact-heading-simple">Let's Work Together</h2>
            <p className="contact-subtext">
              Have a product that needs designing or shipping? Let's talk.
            </p>
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="contact-book-btn">
              <span className="live-dot" />
              Book a 30-min intro call
              <svg width="13" height="13" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                <path d="M3 9L9 3M9 3H4.5M9 3V7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
            <a
              href={`mailto:${EMAIL}`}
              className="contact-email-link"
            >
              or email {EMAIL}
            </a>
          </motion.div>

          <motion.div
            className="social-icons-row"
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <a
              href="https://linkedin.com/in/rashid-lansah"
              className="social-icon-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              <LinkedInIcon size={24} />
            </a>
            <a
              href="https://github.com/RashidLansah"
              className="social-icon-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              <GitHubIcon size={24} />
            </a>
            <a
              href="https://medium.com/@rashidlansah"
              className="social-icon-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              <MediumIcon size={24} />
            </a>
          </motion.div>
        </div>
      </footer>
      </main>
    </div>
  );
}

export default Portfolio;
